import Controller from '@ember/controller';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { dropTask } from 'ember-concurrency';
import type DeviceModel from 'garaje/models/device';
import type DeviceStatusService from 'garaje/services/device-status-service';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

import type { PropertyDevicesRouteModel } from '../../route';

export default class PropertyDevicesIpadsIndexController extends Controller {
  declare model: PropertyDevicesRouteModel;

  @service('device-status-service') declare deviceStatusService: DeviceStatusService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  // Property iPads have their own company-trafficked flag (the visitors flag is
  // location-trafficked, and a split is bound to a single traffic type).
  get isDevicesServiceStatusEnabled(): boolean {
    return this.featureFlags.isEnabled('property-devices-service-status-api');
  }

  // On devices-service-status-api properties the list is driven by live
  // DeviceStatusService telemetry (scoped by zone id under the 'visitors' product)
  // rather than the static model.ipads array, so realtime connection changes and
  // optimistic deletes reflect without a route refresh. Legacy properties keep
  // rendering the v3 records loaded by the route.
  get listableIpads(): DeviceModel[] {
    if (this.isDevicesServiceStatusEnabled) {
      const zoneId = this.model.property.id;
      return Array.from(this.deviceStatusService.statusByDeviceId.values())
        .filter((status) => status.product === 'visitors' && String(status.zoneId) === String(zoneId))
        .map((status) => this.store.peekRecord('device', status.externalId))
        .filter((device): device is DeviceModel => Boolean(device));
    }

    return this.model.ipads.slice();
  }

  deleteDeviceTask = dropTask(async (device: DeviceModel) => {
    let removalSnapshot: ReturnType<DeviceStatusService['removeStatusBackedDevice']> = null;

    try {
      const destroyPromise = device.destroyRecord();
      removalSnapshot = this.isDevicesServiceStatusEnabled
        ? this.deviceStatusService.removeStatusBackedDevice(device.id, {
            zoneId: this.model.property.id,
            // the in-flight destroyRecord owns the device record's lifecycle:
            // it cannot be unloaded mid-flight and unloads itself on success
            unloadDevice: false,
          })
        : null;

      await destroyPromise;
      this.flashMessages.showAndHideFlash('success', 'Successfully removed iPad');

      // Legacy properties rebuild the list from the v3 query; the status-backed
      // list updates reactively once removeStatusBackedDevice prunes the map.
      if (!this.isDevicesServiceStatusEnabled) {
        void this.router.refresh('property.devices.ipads');
      }

      if (this.listableIpads.length === 0) {
        // if we removed the last iPad, redirect to the "new iPad" page
        void this.router.transitionTo('property.devices.ipads.new');
      }
    } catch (e) {
      // The record may already be torn down by the time the rejection surfaces
      // (a `device_deleted` realtime event can unload it first), and rolling back
      // an unloaded record throws. restoreStatusBackedDevice re-pushes it from the
      // snapshot so the list recovers.
      if (!device.isDestroyed && !device.isDestroying) {
        device.rollbackAttributes();
      }
      this.deviceStatusService.restoreStatusBackedDevice(removalSnapshot);
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });
}
